import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader } from 'lucide-react'
import { useEffect, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod/v4'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany } from '@/lib/hooks'
import { GitHubLogo, GitLabLogo } from '@/logos'
import { DockerLogo } from '@/logos/docker'
import type {
  CreateDockerRegistryCredentialMutation,
  CreateDockerRegistryCredentialMutationVariables,
} from '@/routes/registry-credentials/create-registry-credential-sheet/index.types.gen'

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  idCompany: z.string().min(1, 'Company ID is required'),
  registry: z.enum(
    ['custom', 'digitalOcean', 'dockerHub', 'ecr', 'gcr', 'github', 'gitlab'],
    { message: 'Registry is required' }
  ),
  username: z
    .string()
    .min(1, 'Username is required')
    .regex(/^(?=.{1,39}$)[A-Za-z0-9](?:[A-Za-z0-9._-]*[A-Za-z0-9])?$/, {
      message: 'Invalid username',
    }),
  secret: z
    .string()
    .min(8, 'Secret must be at least 8 characters')
    .max(128, 'Secret must be at most 128 characters'),
})

const CREATE_REGISTRY_CREDENTIAL = gql`
  mutation CreateDockerRegistryCredential(
    $idCompany: String!
    $name: String!
    $registry: DockerRegistry!
    $username: String!
    $secret: String!
  ) {
    createDockerRegistryCredential(
      idCompany: $idCompany
      name: $name
      registry: $registry
      username: $username
      secret: $secret
    )
  }
`

type Props = {
  isOpen: boolean
  onClose: () => void
}

export const CreateRegistryCredentialSheet = ({ isOpen, onClose }: Props) => {
  const { idCompany } = useIdCompany()
  const [isPending, startTransition] = useTransition()

  const [createRegistryCredential] = useMutation<
    CreateDockerRegistryCredentialMutation,
    CreateDockerRegistryCredentialMutationVariables
  >(CREATE_REGISTRY_CREDENTIAL)

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      idCompany,
      registry: 'dockerHub',
      name: '',
      username: '',
      secret: '',
    },
  })

  const {
    handleSubmit,
    reset,
    formState: { isValid },
  } = form

  const onSubmit = async (values: z.infer<typeof schema>) => {
    if (isPending || !isValid) {
      return
    }

    startTransition(async () => {
      try {
        await createRegistryCredential({
          variables: {
            idCompany,
            name: values.name,
            registry: values.registry,
            username: values.username,
            secret: values.secret,
          },
        })
        toast(
          <ToasterContent variant="success">
            Registry credential created successfully
          </ToasterContent>,
          { position: 'top-right' }
        )
        onClose()
      } catch {
        toast(
          <ToasterContent variant="error">
            Failed to create registry credential
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  useEffect(() => {
    if (!isOpen) {
      reset()
    }
  }, [isOpen, reset])

  return (
    <Sheet open={isOpen} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>Create registry credential</SheetTitle>
        </SheetHeader>
        <div className="px-4 overflow-y-auto w-full pb-2">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="off"
                        aria-label="Registry credential name"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="registry"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Registry</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger className="h-8 bg-background shadow-sm w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent side="top">
                          <SelectItem value="dockerHub">
                            <DockerLogo /> Docker Hub
                          </SelectItem>
                          <SelectItem value="github">
                            <GitHubLogo />
                            GitHub Container Registry
                          </SelectItem>
                          <SelectItem value="gitlab">
                            <GitLabLogo /> GitLab Container Registry
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Username</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="off"
                        aria-label="Username"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="secret"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Personal access token</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="off"
                        aria-label="Personal access token"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" hidden />
            </form>
          </Form>
        </div>
        <SheetFooter className="sticky">
          <Button disabled={isPending} onClick={handleSubmit(onSubmit)}>
            {isPending ? <Loader className="animate-spin" /> : null}
            Create registry credential
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
